import { type DependencyList, useEffect } from 'react';

import { useIsMountedGetter } from './useIsMountedGetter';

/**
 * Run an asynchronous effect function while keeping track of the component
 * mounted state.
 *
 * @param effect - Async effect to run. Is passed an `isActive` function that
 *   returns a boolean indicating if the current effect callback is the latest
 *   one. Also passed a `isMounted` function that returns a boolean indicating
 *   if the component is still mounted. One of them should be used before
 *   setting any state to avoid triggering renders in unmounted components.
 * @param [dependencies] - Dependencies, defaults to empty array.
 * @param [cleanup] - Function that runs during clean-up. Is passed
 *   the resulting value of the effect itself (can be an error).
 * @example
 *
 * function Component() {
 *   const [state, setState] = useState('foo');
 *
 *   useAsyncEffect(async (isActive, isMounted) => {
 *     try {
 *       const newState = await processState(state);
 *       // Or isMounted() depending on scenario.
 *       if (isActive()) {
 *         setState(newState);
 *       }
 *     } catch (err) {
 *       if (isActive()) {
 *         setState(err);
 *       }
 *     }
 *   }, [state]);
 *
 *   return <div>{state}</div>;
 * }
 */
export function useAsyncEffect<T>(
	effect: (params: {
		/** If the current effect callback is the latest one. */
		isActive: () => boolean;
		/** If the component is still mounted. */
		isMounted: () => boolean;
	}) => Promise<T>,
	dependencies: DependencyList = [],
	cleanup: ((result: T) => void) | null = null,
) {
	const isMounted = useIsMountedGetter();

	useEffect(() => {
		let result: T;
		let isActive = true;

		Promise.resolve(effect({ isActive: () => isActive, isMounted }))
			.then((value) => {
				result = value;
			})
			.catch((error) => {
				result = error;
			});

		return () => {
			isActive = false;
			cleanup?.(result);
		};
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, dependencies);
}
